import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, CardBody, Col, Container, Form, FormGroup, Input, Label, Modal, ModalBody, Row, Table } from "reactstrap";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import withRouter from "components/Common/withRouter";
import { urlEndPoint } from "helpers/GeneralHelper";
import {
  getClientReviewForEditByClientId,
  getClientReviewHireCandidates,
  submitClientReviewFilterData,
  submitUpsertData,
} from "model/ClientReviewModel";
import moment from "moment";
import { resolveLoggedInUserTypeId } from "pages/Dashboard/RoleBasedDashboard";
import { usePermissionContext } from "libs/context/PermissionContext";

const SUPER_ADMIN_USER_TYPE_IDS = new Set([1, 2, 3, 4, 11]);
const ACCOUNT_EXECUTIVE_USER_TYPE_ID = 12;

const STATUS_OPTIONS = [
  { id: "1", name: "red" },
  { id: "2", name: "yellow" },
  { id: "3", name: "green" },
];

const ALL_CANDIDATES_PREFILL_STORAGE_KEY = "allCandidatesPrefillFilters";

const extractHireCandidateList = (response) => {
  if (response == null) return [];
  const raw = response?.data;
  if (Array.isArray(raw)) return raw;
  if (raw && Array.isArray(raw.records)) return raw.records;
  if (raw && Array.isArray(raw.data)) return raw.data;
  return [];
};

const parseHiredFlag = (value) => {
  if (value === true || value === 1) return true;
  if (value === false || value === 0 || value == null || value === "") return false;
  const normalized = String(value).trim().toLowerCase();
  return normalized === "true" || normalized === "yes" || normalized === "1";
};

const normalizeHireCandidateRow = (item = {}, index = 0) => {
  const candidateClientId = String(
    item?.candidateClientId ??
    item?.CandidateClientId ??
    item?.id ??
    item?.candidateId ??
    ""
  ).trim();

  return {
    candidateClientId,
    id: candidateClientId,
    sn: index + 1,
    hired: parseHiredFlag(
      item?.isHired ?? item?.IsHired ?? item?.hired ?? item?.Hired ?? item?.selected
    ),
    candidate:
      item?.candidateName ??
      item?.candidate ??
      item?.name ??
      item?.fullName ??
      "",
    email: item?.email ?? item?.candidateEmail ?? "",
    phone:
      item?.phone ??
      item?.candidatePhone ??
      item?.phoneNumber ??
      item?.candidatePhoneFormat ??
      "",
  };
};

const getHiredCandidateClientIds = (rows = []) =>
  (Array.isArray(rows) ? rows : [])
    .filter((row) => Boolean(row?.hired))
    .map((row) => String(row?.candidateClientId ?? row?.id ?? "").trim())
    .filter(Boolean);

const appendHiredCandidateClientIdsToFormData = (formData, rows = []) => {
  const hiredCandidateClientIds = getHiredCandidateClientIds(rows);
  hiredCandidateClientIds.forEach((candidateClientId, index) => {
    formData.append(`HiredCandidateClientIds[${index}]`, candidateClientId);
  });
};

const appendPreviouslyHiredCandidateClientIdsToFormData = (formData, rows = []) => {
  const previouslyHiredIds = getHiredCandidateClientIds(rows);
  previouslyHiredIds.forEach((candidateClientId, index) => {
    formData.append(`PreviouslyHiredCandidateClientIds[${index}]`, candidateClientId);
  });
};

const normalizeHireCandidateRows = (list = []) =>
  (Array.isArray(list) ? list : []).map(normalizeHireCandidateRow);

const countHiredCandidates = (rows = []) =>
  (Array.isArray(rows) ? rows : []).filter((row) => Boolean(row?.hired)).length;

const applyHiresMonthToForm = (previousForm, hiresMonth) => {
  const nextCount = Math.max(0, Number(hiresMonth || 0));
  return {
    ...previousForm,
    hiresMonth: nextCount,
    updateSent: nextCount,
    UpdateSent: nextCount,
  };
};

const INITIAL_FORM = {
  ClientReviewId: "",
  ClientId: "",
  ReviewStatusId: "",
  Requirements: "",
  Notes: "",
  ContactByPhone: false,
  ContactByText: false,
  ContactByEmail: false,
  ReviewImageId: "",
  UpdateSent: "",
  LastStatusId: "",
  ReviewImageFile: null,
  ReviewImageUrl: "",
  id: "",
  client: null,
  clientId: "",
  contactBy: "",
  lastStatus: "",
  link: "",
  reviewImageId: 0,
  recruiter: null,
  submitBy: null,
  updateSent: 0,
  week: "",
  status: "",
  requirements: "",
  notes: "",
  reviewImage: null,
  reviewImageUrl: "",
  hiresMonth: 0,
  phone: false,
  text: false,
  email: false,
};

const getContactFlags = (item = {}) => {
  const rawContact = String(item?.contactBy || item?.contact_by || "").toLowerCase().trim();
  const normalized = rawContact.replace(/\s+/g, "");

  return {
    phone: Boolean(item?.contactByPhone) || Boolean(item?.contact_by_phone) || normalized.includes("phone"),
    text: Boolean(item?.contactByText) || Boolean(item?.contact_by_text) || normalized.includes("text"),
    email: Boolean(item?.contactByEmail) || Boolean(item?.contact_by_email) || normalized.includes("email"),
  };
};

const getStatusBadgeClass = (statusValue) => {
  const normalized = String(statusValue || "").toLowerCase();
  if (normalized === "red") return "bg-danger";
  if (normalized === "yellow") return "bg-warning";
  if (normalized === "green") return "bg-success";
  return "bg-secondary";
};

const getStatusTextClass = (statusValue) => {
  const normalized = String(statusValue || "").toLowerCase();
  if (normalized === "red") return "client-review-red";
  if (normalized === "yellow") return "client-review-yellow";
  if (normalized === "green") return "client-review-green";
  return "";
};

const getStatusHistoryCardClass = (statusValue) => {
  const normalized = String(statusValue || "").toLowerCase();
  if (normalized === "red") return "client-review-history-card--red";
  if (normalized === "yellow") return "client-review-history-card--yellow";
  if (normalized === "green") return "client-review-history-card--green";
  return "";
};

const normalizeReviewRecord = (item = {}) => ({
  id: item?.id || item?._id || "",
  clientReviewId: item?.clientReviewId || item?.client_review_id || item?.id || item?._id || "",
  clientId: item?.clientId || item?.client_id || "",
  client: item?.client || item?.clientName || item?.company || "",
  week: item?.week || "",
  fromDate: item?.fromDate || item?.from_date || item?.startDate || item?.weekStart || "",
  toDate: item?.toDate || item?.to_date || item?.endDate || item?.weekEnd || "",
  status: item?.status || "",
  reviewStatusId: item?.reviewStatusId || item?.review_status_id || item?.statusId || "",
  lastStatus: item?.lastStatus || item?.last_status || "",
  lastStatusId: item?.lastStatusId || item?.last_status_id || "",
  requirements: item?.requirements || "",
  notes: item?.notes || item?.note || "",
  reviewImageUrl:
    item?.reviewImageUrl ||
    item?.ReviewImageUrl ||
    item?.reviewImageLink ||
    item?.reviewImage ||
    item?.ReviewImage ||
    item?.imageLink ||
    item?.link ||
    "",
  link:
    item?.link ||
    item?.Link ||
    item?.reviewImageLink ||
    item?.imageLink ||
    item?.reviewImage ||
    item?.ReviewImage ||
    item?.reviewImageUrl ||
    item?.ReviewImageUrl ||
    "",
  reviewImageId: Number(item?.reviewImageId || item?.review_image_id || 0),
  reviewImageFile: item?.reviewImageFile || null,
  submitBy: item?.submitBy || item?.submit_by || null,
  updateSent: Number(item?.updateSent ?? item?.update_sent ?? item?.updateSentThisMonth ?? 0),
  hiresMonth: Number(item?.hiresMonth || item?.updateSentThisMonth || item?.updateSent || 0),
  contactBy: item?.contactBy || item?.contact_by || "",
  contactByPhone: Boolean(item?.contactByPhone ?? item?.contact_by_phone ?? false),
  contactByText: Boolean(item?.contactByText ?? item?.contact_by_text ?? false),
  contactByEmail: Boolean(item?.contactByEmail ?? item?.contact_by_email ?? false),
  recruiter: item?.recruiter || "",
});

const getDownloadUrl = (value) => {
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;
  const baseUrl = String(process.env.REACT_APP_LINK_URL || "").replace(/\/+$/, "");
  const linkPath = String(value).replace(/^\/+/, "");
  return baseUrl ? `${baseUrl}/${linkPath}` : `/${linkPath}`;
};

const looksLikeHtmlString = (value) =>
  value != null && /<[a-z][\s\S]*>/i.test(String(value));

const resolvePreviousReviewImageUrl = (item = {}) =>
  getDownloadUrl(
    item?.link ||
    item?.reviewImageUrl ||
    item?.reviewImageLink ||
    item?.reviewImage ||
    item?.imageLink
  );

const renderPreviousReviewNotesContent = (item = {}) => {
  const notesText = String(item?.notes ?? "").trim();
  if (!notesText) {
    return <span className="text-muted">—</span>;
  }
  if (looksLikeHtmlString(notesText)) {
    return (
      <span
        className="client-review-history-notes-html"
        dangerouslySetInnerHTML={{ __html: notesText }}
      />
    );
  }
  return notesText;
};

const getStatusIdByName = (name) =>
  STATUS_OPTIONS.find(
    (item) => String(item?.name || "").toLowerCase() === String(name || "").toLowerCase()
  )?.id || "";

const getStatusNameById = (id) =>
  STATUS_OPTIONS.find((item) => String(item?.id) === String(id || ""))?.name || "";

const formatContactMethod = (item = {}) => {
  const flags = getContactFlags(item);
  const methods = [];
  if (flags.phone) methods.push("Phone");
  if (flags.text) methods.push("Text");
  if (flags.email) methods.push("Email");
  if (methods.length) return methods.join(", ");
  return item?.contactBy || "-";
};

const formatReviewWeekLabel = (item = {}) => {
  if (item?.week) return item.week;
  const from = item?.fromDate || "";
  const to = item?.toDate || "";
  if (from && to) {
    return `${moment(from).format("MM-DD-YYYY")} to ${moment(to).format("MM-DD-YYYY")}`;
  }
  return from || to || "-";
};

const ReviewImageDownloadLink = ({ href, className = "" }) => {
  const downloadUrl = getDownloadUrl(href);
  if (!downloadUrl) return null;

  return (
    <a
      href={downloadUrl}
      target="_blank"
      rel="noreferrer"
      download
      className={`client-review-download-link mt-2 ${className}`.trim()}
    >
      <i className="mdi mdi-download" aria-hidden="true" />
      <span>Download review image</span>
    </a>
  );
};

const ClientReviewEdit = () => {
  const { permissionData } = usePermissionContext() || {};
  const location = useLocation();
  const navigate = useNavigate();
  const { clientId: clientIdFromParams } = useParams();
  const clientId = String(
    clientIdFromParams || location?.state?.clientId || ""
  ).trim();

  const canUpdate = Boolean(permissionData?.ClientReviewUpdateAccess);

  const [loading, setLoading] = useState(false);
  const [clientName, setClientName] = useState("");
  const [weekRange, setWeekRange] = useState({ fromDate: "", toDate: "" });
  const [previousReviews, setPreviousReviews] = useState([]);
  const [contactMethodKey, setContactMethodKey] = useState(0);
  const [isCandidateModalOpen, setIsCandidateModalOpen] = useState(false);
  const [candidateModalMode, setCandidateModalMode] = useState("thisMonth");
  const [hireCandidatesLoading, setHireCandidatesLoading] = useState(false);
  const [hireCandidates, setHireCandidates] = useState([]);
  const [lastMonthHireCandidates, setLastMonthHireCandidates] = useState([]);
  const [hireCandidatesModalKey, setHireCandidatesModalKey] = useState(0);
  const [hiresMonthBaseline, setHiresMonthBaseline] = useState(0);
  const [hireCandidatesInitialCount, setHireCandidatesInitialCount] = useState(0);
  const [imagePreviewModalOpen, setImagePreviewModalOpen] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState("");
  const [previewImageAlt, setPreviewImageAlt] = useState("Review image");
  const [isReviewImageDragging, setIsReviewImageDragging] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);

  const openImagePreview = (url, alt = "Review image") => {
    const resolved = getDownloadUrl(url);
    if (!resolved) return;
    setPreviewImageAlt(alt);
    setPreviewImageUrl(resolved);
    setImagePreviewModalOpen(true);
  };

  const closeImagePreview = () => {
    setImagePreviewModalOpen(false);
    setPreviewImageUrl("");
    setPreviewImageAlt("Review image");
  };

  const toggleCandidateModal = () => {
    setIsCandidateModalOpen((prev) => !prev);
  };

  const openCandidateModal = (mode, rows = []) => {
    const normalizedRows = normalizeHireCandidateRows(rows);

    if (mode === "lastMonth") {
      setLastMonthHireCandidates(normalizedRows);
    } else {
      const initialHiredCount = countHiredCandidates(normalizedRows);
      const currentCount = Number(
        form?.hiresMonth ?? form?.updateSent ?? form?.UpdateSent ?? 0
      );
      setHiresMonthBaseline(currentCount);
      setHireCandidatesInitialCount(initialHiredCount);
      setHireCandidates(normalizedRows);
    }

    setCandidateModalMode(mode);
    setHireCandidatesModalKey((previousKey) => previousKey + 1);
    setIsCandidateModalOpen(true);
  };

  const handleOpenHireCandidatesModal = async () => {
    if (hireCandidates.length) {
      openCandidateModal("thisMonth", hireCandidates);
      return;
    }

    const resolvedClientId = String(
      form?.ClientId || form?.clientId || clientId || ""
    ).trim();
    if (!resolvedClientId) {
      toast.error("Client id is missing");
      return;
    }

    setHireCandidatesLoading(true);
    try {
      const response = await getClientReviewHireCandidates(resolvedClientId);
      if (!response?.success) {
        toast.error(response?.message || "Unable to load hire candidates");
        return;
      }
      const list = extractHireCandidateList(response);
      openCandidateModal("thisMonth", list);
    } finally {
      setHireCandidatesLoading(false);
    }
  };

  const handleOpenLastMonthHireCandidatesModal = () => {
    openCandidateModal("lastMonth", lastMonthHireCandidates);
  };

  const handleViewPipeline = () => {
    const resolvedClientId = String(
      form?.ClientId || form?.clientId || clientId || ""
    ).trim();
    const resolvedClientName = String(
      clientName || form?.client || form?.clientName || ""
    ).trim();

    if (!resolvedClientId) {
      toast.error("Client id is missing");
      return;
    }

    const prefillFilters = {
      clientId: resolvedClientId,
      clientIdLabel: resolvedClientName,
      clientName: resolvedClientName,
      profileStatus: "completed",
    };

    try {
      sessionStorage.setItem(
        ALL_CANDIDATES_PREFILL_STORAGE_KEY,
        JSON.stringify(prefillFilters)
      );
    } catch {
      // navigation state remains the fallback
    }

    navigate(`/${urlEndPoint.allCandidates}`, {
      state: { prefillFilters },
    });
  };

  const activeCandidateModalRows =
    candidateModalMode === "lastMonth" ? lastMonthHireCandidates : hireCandidates;

  const handleHireCandidateCheckboxChange = (rowIndex, checked) => {
    if (candidateModalMode === "lastMonth") {
      setLastMonthHireCandidates((previousRows) =>
        previousRows.map((row, index) =>
          index === rowIndex ? { ...row, hired: checked } : row
        )
      );
      return;
    }

    const nextRows = hireCandidates.map((row, index) =>
      index === rowIndex ? { ...row, hired: checked } : row
    );
    const nextHiresMonth =
      Number(hiresMonthBaseline) +
      (countHiredCandidates(nextRows) - hireCandidatesInitialCount);

    setHireCandidates(nextRows);
    setForm((previousForm) =>
      applyHiresMonthToForm(previousForm, nextHiresMonth)
    );
  };

  const fetchReviewData = async () => {
    if (!clientId) {
      toast.error("Client id is missing");
      return;
    }

    setLoading(true);
    try {
      const [currentRangeRes, previousRes] = await Promise.all([
        getClientReviewForEditByClientId(clientId),
        submitClientReviewFilterData({ clientId, pageSize: 5 }),
      ]);

      if (!currentRangeRes?.success) {
        toast.error(currentRangeRes?.message || "Unable to load client review");
        return;
      }

      const editData = currentRangeRes?.data || {};
      const saveDto = editData?.clientReviewSaveDTO ?? null;
      const hireCandidatesList = normalizeHireCandidateRows(editData?.hireCandidates);
      const lastMonthHiresList = normalizeHireCandidateRows(editData?.lastMonthHires);
      const filterPreviousList = previousRes?.data?.records || previousRes?.data || [];
      const editPreviousList = Array.isArray(editData?.previousReviews)
        ? editData.previousReviews
        : [];
      // Prefer edit API previousReviews — includes notes + reviewImageLink
      const previousReviewList = Array.isArray(editPreviousList) && editPreviousList.length
        ? editPreviousList
        : filterPreviousList;

      setHireCandidates(hireCandidatesList);
      setLastMonthHireCandidates(lastMonthHiresList);
      setPreviousReviews(
        Array.isArray(previousReviewList)
          ? previousReviewList.map(normalizeReviewRecord)
          : []
      );

      const current = saveDto ? normalizeReviewRecord(saveDto) : {
        clientId: editData?.clientReviewSaveDTO?.clientId || clientId,
        client: editData?.clientName || "",
      };
      const contactFlags = getContactFlags(current);
      const resolvedStatusName = String(current?.status || current?.lastStatus || "").toLowerCase();
      const resolvedLastStatusId = current?.lastStatusId || getStatusIdByName(current?.lastStatus);
      const hiresThisMonthCount = Number(
        editData?.hiresThisMonthCount ??
        current?.hiresMonth ??
        current?.updateSent ??
        countHiredCandidates(hireCandidatesList) ??
        0
      );

      setWeekRange({
        fromDate: editData?.weekStart || current?.fromDate || "",
        toDate: editData?.weekEnd || current?.toDate || "",
      });

      setClientName(editData?.clientName || current?.client || "");
      setForm({
        ClientReviewId: current?.clientReviewId || current?.id || "",
        ClientId: current?.clientId || saveDto?.clientId || clientId || "",
        ReviewStatusId: current?.reviewStatusId || getStatusIdByName(resolvedStatusName) || "",
        Requirements: current?.requirements || saveDto?.requirements || "",
        Notes: current?.notes || saveDto?.notes || "",
        ContactByPhone: Boolean(current?.contactByPhone ?? contactFlags.phone),
        ContactByText: Boolean(current?.contactByText ?? contactFlags.text),
        ContactByEmail: Boolean(current?.contactByEmail ?? contactFlags.email),
        ReviewImageId: String(current?.reviewImageId ?? saveDto?.reviewImageId ?? ""),
        UpdateSent: hiresThisMonthCount,
        LastStatusId: current?.lastStatusId || resolvedLastStatusId || "",
        ReviewImageFile: null,
        ReviewImageUrl: getDownloadUrl(current?.reviewImageUrl || current?.link || ""),
        id: current?.id || "",
        client: current?.client || editData?.clientName || null,
        clientId: current?.clientId || saveDto?.clientId || clientId,
        contactBy: current?.contactBy || "",
        lastStatus: resolvedLastStatusId,
        link: current?.link || "",
        reviewImageId: Number(current?.reviewImageId || 0),
        recruiter: current?.recruiter || null,
        submitBy: current?.submitBy || null,
        updateSent: hiresThisMonthCount,
        week: editData?.weekDaysText || current?.week || "",
        status: resolvedStatusName,
        requirements: current?.requirements || saveDto?.requirements || "",
        notes: current?.notes || saveDto?.notes || "",
        reviewImageUrl: getDownloadUrl(current?.reviewImageUrl || current?.link || ""),
        reviewImage: null,
        hiresMonth: hiresThisMonthCount,
        phone: contactFlags.phone,
        text: contactFlags.text,
        email: contactFlags.email,
      });
      setContactMethodKey((prev) => prev + 1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviewData();
  }, [clientId]);

  const reviewRangeLabel = useMemo(() => {
    if (form?.week) return form.week;
    const from = weekRange?.fromDate || "";
    const to = weekRange?.toDate || "";
    return from && to ? `${moment(from).format("MM-DD-YYYY")} to ${moment(to).format("MM-DD-YYYY")}` : "-";
  }, [form?.week, weekRange]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canUpdate) return;

    const formData = new FormData();
    const payload = {
      ClientReviewId: form?.ClientReviewId || form?.clientReviewId || form?.id || "",
      ClientId: form?.ClientId || form?.clientId || clientId || "",
      // WeekStart: weekRange?.fromDate || "",
      // WeekEnd: weekRange?.toDate || "",
      ReviewStatusId: form?.ReviewStatusId || getStatusIdByName(form?.status),
      Requirements: form?.Requirements || form?.requirements || "",
      Notes: form?.Notes || form?.notes || "",
      ContactByPhone: Boolean(form?.ContactByPhone ?? form?.phone),
      ContactByText: Boolean(form?.ContactByText ?? form?.text),
      ContactByEmail: Boolean(form?.ContactByEmail ?? form?.email),
      ReviewImageId: Number(form?.ReviewImageId || form?.reviewImageId || 0),
      UpdateSent: Number(form?.UpdateSent ?? form?.updateSent ?? form?.hiresMonth ?? 0),
      LastStatusId: form?.LastStatusId || form?.lastStatus || "",
    };

    Object.entries(payload).forEach(([key, value]) => {
      formData.append(key, value);
    });

    appendHiredCandidateClientIdsToFormData(formData, hireCandidates);
    appendPreviouslyHiredCandidateClientIdsToFormData(formData, lastMonthHireCandidates);

    if (form?.ReviewImageFile instanceof File) {
      formData.append("ReviewImageFile", form.ReviewImageFile);
    } else if (form?.reviewImage instanceof File) {
      formData.append("ReviewImageFile", form.reviewImage);
    }

    setLoading(true);
    try {
      const response = await submitUpsertData(formData);
      if (response?.success) {
        toast.success(response?.message || "Client review updated successfully");

        const userTypeId = resolveLoggedInUserTypeId();
        if (userTypeId === ACCOUNT_EXECUTIVE_USER_TYPE_ID) {
          navigate("/dashboard");
          return;
        }
        if (SUPER_ADMIN_USER_TYPE_IDS.has(userTypeId)) {
          navigate(`/${urlEndPoint.clientReview}`);
          return;
        }

        await fetchReviewData();
      } else {
        toast.error(response?.message || "Unable to update client review");
      }
    } finally {
      setLoading(false);
    }
  };

  const applyReviewImageFile = (file) => {
    if (!file) return;
    const isImage =
      /^image\//i.test(file.type) ||
      /\.(jpe?g|png|gif|bmp|webp|svg|tiff?|heic|heif|avif)$/i.test(file.name || "");
    if (!isImage) {
      toast.error("Please select an image file");
      return;
    }
    setForm((prev) => ({
      ...prev,
      reviewImage: file,
      ReviewImageFile: file,
    }));
  };

  const handleReviewImageDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsReviewImageDragging(false);
    const droppedFile = e?.dataTransfer?.files?.[0] || null;
    applyReviewImageFile(droppedFile);
  };

  const handleReviewImageDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isReviewImageDragging) setIsReviewImageDragging(true);
  };

  const handleReviewImageDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsReviewImageDragging(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : type === "file" ? files?.[0] || null : value,
      ...(name === "status" ? { ReviewStatusId: getStatusIdByName(value || "") } : {}),
      ...(name === "ReviewStatusId" ? { status: getStatusNameById(value || "") } : {}),
      ...(name === "requirements" ? { Requirements: value } : {}),
      ...(name === "Requirements" ? { requirements: value } : {}),
      ...(name === "notes" ? { Notes: value } : {}),
      ...(name === "Notes" ? { notes: value } : {}),
      ...(name === "phone" ? { ContactByPhone: checked } : {}),
      ...(name === "text" ? { ContactByText: checked } : {}),
      ...(name === "email" ? { ContactByEmail: checked } : {}),
      ...(name === "ContactByPhone" ? { phone: checked } : {}),
      ...(name === "ContactByText" ? { text: checked } : {}),
      ...(name === "ContactByEmail" ? { email: checked } : {}),
      ...(name === "reviewImage" ? { ReviewImageFile: files?.[0] || null } : {}),
      ...(name === "ReviewImageFile" ? { reviewImage: files?.[0] || null } : {}),
      ...(name === "reviewImageId" ? { ReviewImageId: value } : {}),
      ...(name === "ReviewImageId" ? { reviewImageId: value } : {}),
      ...(name === "updateSent" ? { UpdateSent: value } : {}),
      ...(name === "UpdateSent" ? { updateSent: Number(value || 0), hiresMonth: Number(value || 0) } : {}),
      ...(name === "hiresMonth" ? { updateSent: Number(value || 0), UpdateSent: value } : {}),
    }));
  };

  return (
    <div className="page-content client-review-edit-wrap">
      <Container fluid>
        <Card className="client-review-edit-page border mb-3">
          <CardBody>
            <h4 className="mb-2">{clientName || "Client Review"}</h4>
            <p className="text-muted mb-0">Weekly Review : {reviewRangeLabel}</p>
            <hr />

            <Row className="client-review-edit-split g-3 g-lg-4">
              <Col lg={7} xl={7}>
                <section className="client-review-panel client-review-panel--current">
                  <div className="client-review-panel__header d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                    <h5 className="mb-0">Client Review</h5>
                    <div className="d-flex flex-wrap gap-2">
                      <Button
                        type="button"
                        color="primary"
                        onClick={handleOpenLastMonthHireCandidatesModal}
                      >
                        Hires Last Month ({lastMonthHireCandidates?.length || 0})
                      </Button>
                      <Button
                        type="button"
                        color="primary"
                        outline
                        onClick={handleViewPipeline}
                      >
                        View Pipeline
                      </Button>
                    </div>
                  </div>
                  <Form onSubmit={handleSubmit}>
                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label>Status</Label>
                          <Input
                            type="select"
                            name="ReviewStatusId"
                            value={form.ReviewStatusId || ""}
                            onChange={handleInputChange}
                          >
                            <option value="">Select Status</option>
                            {STATUS_OPTIONS.map((item) => (
                              <option key={item.id} value={item.id}>
                                {item.name.charAt(0).toUpperCase() + item.name.slice(1)}
                              </option>
                            ))}
                          </Input>
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label>Requirements</Label>
                          <Input
                            type="text"
                            placeholder=""
                            name="requirements"
                            value={form.requirements}
                            onChange={handleInputChange}
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row className="client-review-notes-row">
                      <Col md={6} className="client-review-notes-col">
                        <FormGroup className="client-review-notes-field mb-0">
                          <Label>Notes</Label>
                          <Input
                            type="textarea"
                            className="client-review-notes-input"
                            name="notes"
                            value={form.notes}
                            onChange={handleInputChange}
                          />
                        </FormGroup>
                      </Col>
                      <Col md={6} className="client-review-notes-col">
                        <FormGroup className="client-review-image-field mb-0">
                          <Label>Review Image</Label>
                          <div className="client-review-file-wrap">
                            <label
                              className={`client-review-file-picker${form.reviewImage ? " has-file" : ""}${isReviewImageDragging ? " is-dragging" : ""}`}
                              onDrop={handleReviewImageDrop}
                              onDragOver={handleReviewImageDragOver}
                              onDragEnter={handleReviewImageDragOver}
                              onDragLeave={handleReviewImageDragLeave}
                            >
                              <input
                                type="file"
                                className="client-review-file-input"
                                accept="image/*"
                                name="reviewImage"
                                onChange={handleInputChange}
                              />
                              <i className="fa fa-image" aria-hidden="true" />
                              <span className="client-review-file-picker-text">
                                {isReviewImageDragging
                                  ? "Drop image here"
                                  : form.reviewImage?.name || "Choose or drag & drop file"}
                              </span>
                            </label>
                          </div>
                          {form.reviewImageUrl ? (
                            <ReviewImageDownloadLink href={form.reviewImageUrl} />
                          ) : null}
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <FormGroup className="move-hires-month mt-2">
                          <Label>Hires This Month</Label>
                          <div className="d-flex align-items-center">
                            <Input
                              type="number"
                              value={Number(form.hiresMonth ?? 0)}
                              name="hiresMonth"
                              readOnly
                              disabled
                            />
                            <Button
                              type="button"
                              color="primary"
                              className="ms-2"
                              onClick={handleOpenHireCandidatesModal}
                              disabled={hireCandidatesLoading}
                            >
                              {hireCandidatesLoading ? "..." : "+"}
                            </Button>
                          </div>
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label>Contact Method</Label>
                          <div key={contactMethodKey} className="d-flex flex-wrap gap-3">
                            <Label check className="mb-0">
                              <Input
                                type="checkbox"
                                name="phone"
                                defaultChecked={form.phone}
                                onChange={handleInputChange}
                              />{" "}
                              Phone
                            </Label>
                            <Label check className="mb-0">
                              <Input
                                type="checkbox"
                                name="text"
                                defaultChecked={form.text}
                                onChange={handleInputChange}
                              />{" "}
                              Text
                            </Label>
                            <Label check className="mb-0">
                              <Input
                                type="checkbox"
                                name="email"
                                defaultChecked={form.email}
                                onChange={handleInputChange}
                              />{" "}
                              Email
                            </Label>
                          </div>
                        </FormGroup>
                      </Col>
                    </Row>
                    {canUpdate ? (
                      <div className="text-end">
                        <Button color="primary" className="mt-3" disabled={loading}>
                          {loading ? "Updating..." : "Update"}
                        </Button>
                      </div>
                    ) : null}
                  </Form>
                </section>
              </Col>

              <Col lg={5} xl={5}>
                <section className="client-review-panel client-review-panel--history">
                  <h5 className="mb-3">Previous Review</h5>
                  {previousReviews?.length ? (
                    <div className="client-review-history-list">
                      {previousReviews.map((item) => {
                        const imageUrl = resolvePreviousReviewImageUrl(item);
                        return (
                          <div
                            key={item?.id || item?.week || `${item?.fromDate}-${item?.toDate}`}
                            className={`client-review-history-card p-3 mb-3 ${getStatusHistoryCardClass(item?.status)}`}
                          >
                            <p className={`mb-2 ${getStatusTextClass(item?.status)}`}>
                              <strong>Review Date :</strong> {formatReviewWeekLabel(item)}
                            </p>
                            <Row>
                              <Col md={12} lg={12}>
                                <p className="mb-1">
                                  <strong>Status :</strong>{" "}
                                  <span className={`badge ${getStatusBadgeClass(item?.status)}`}>
                                    {item?.status || "-"}
                                  </span>
                                </p>
                                <p className="mb-1">
                                  <strong>Previous Status :</strong> {item?.lastStatus || "-"}
                                </p>
                                <p className="mb-1">
                                  <strong>Contact Method :</strong> {formatContactMethod(item)}
                                </p>
                                <p className="mb-1">
                                  <strong>Requirements :</strong> {item?.requirements || "-"}
                                </p>
                                <p className="mb-1">
                                  <strong>Recruiter :</strong> {item?.recruiter || "-"}
                                </p>
                                <p className="mb-1">
                                  <strong>Update Sent This Month :</strong> {item?.hiresMonth ?? 0}
                                </p>
                                <p className="mb-2 client-review-history-notes">
                                  <strong>Notes :</strong> {renderPreviousReviewNotesContent(item)}
                                </p>
                              </Col>
                              {imageUrl ? (
                                <Col md={12} lg={12}>
                                  <img
                                    src={imageUrl}
                                    alt="Review"
                                    className="client-review-history-card__image img-fluid border"
                                    role="button"
                                    tabIndex={0}
                                    onClick={() =>
                                      openImagePreview(
                                        imageUrl,
                                        `Review ${formatReviewWeekLabel(item)}`
                                      )
                                    }
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        openImagePreview(
                                          imageUrl,
                                          `Review ${formatReviewWeekLabel(item)}`
                                        );
                                      }
                                    }}
                                  />
                                  <ReviewImageDownloadLink href={imageUrl} />
                                </Col>
                              ) : null}
                            </Row>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-muted mb-0">No previous review found.</p>
                  )}
                </section>
              </Col>
            </Row>
          </CardBody>
        </Card>

        <Modal
          isOpen={imagePreviewModalOpen}
          toggle={closeImagePreview}
          backdrop="static"
          className="modal-full-height"
          centered
          size="lg"
        >
          <ModalBody className="p-4" style={{ maxHeight: "95vh", overflowY: "auto" }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="fw-bold mb-0">Preview Image</h5>
              <Button close onClick={closeImagePreview} />
            </div>
            <hr />
            <div className="text-center">
              <img
                src={previewImageUrl}
                alt={previewImageAlt}
                className="client-review-preview-image"
              />
              <div className="d-flex justify-content-center mt-2">
                <ReviewImageDownloadLink href={previewImageUrl} />
              </div>
            </div>
          </ModalBody>
        </Modal>

        <Modal
          isOpen={isCandidateModalOpen}
          toggle={toggleCandidateModal}
          backdrop="static"
          centered
          size="xl"
          zIndex={5000}
          className="modal-full-height question-type-modal"
        >
          <ModalBody className="p-4" style={{ maxHeight: "95vh", overflowY: "auto" }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="fw-bold mb-0">
                {candidateModalMode === "lastMonth"
                  ? "Select Candidate - Hires Last Month"
                  : "Select Candidate"}
              </h5>
              <Button close onClick={toggleCandidateModal} />
            </div>
            <hr />

            <Table bordered responsive className="mb-0">
              <thead>
                <tr>
                  <th style={{ width: "60px" }}>SN</th>
                  <th style={{ width: "90px" }}>Hired</th>
                  <th>Candidate</th>
                  <th>Email</th>
                  <th>Phone</th>
                </tr>
              </thead>
              <tbody key={hireCandidatesModalKey}>
                {activeCandidateModalRows.length ? (
                  activeCandidateModalRows.map((item, rowIndex) => (
                    <tr key={item.candidateClientId || item.id || item.sn}>
                      <td>{item.sn}</td>
                      <td>
                        <Input
                          type="checkbox"
                          name="hired"
                          defaultChecked={Boolean(item.hired)}
                          onChange={(event) =>
                            handleHireCandidateCheckboxChange(
                              rowIndex,
                              event.target.checked
                            )
                          }
                        />
                      </td>
                      <td>{item.candidate}</td>
                      <td>{item.email}</td>
                      <td>{item.phone}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="text-center text-muted">
                      No hire candidates found.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </ModalBody>
        </Modal>
      </Container>
    </div>
  );
};

export default withRouter(ClientReviewEdit);
